import { publicApiConfig } from '#apps/client-web/app/config/public-api';

import { type SerializedAudit } from '#packages/data-collection/models/audit';
import { type AuditForm } from '#packages/data-collection/models/audit-form';

export type SyncAuditResult = {
  id: string;
};

export type FileContent = Blob | ArrayBuffer;

export type SyncApiServiceOptions = {
  baseUrl: string;
};

export class SyncApiService {
  private readonly opts: SyncApiServiceOptions;

  constructor(opts: SyncApiServiceOptions) {
    this.opts = opts;
  }

  private buildHeaders(
    accessToken: string | undefined,
    extra?: Record<string, string>,
  ): HeadersInit {
    return {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...extra,
    };
  }

  async syncAudit({
    audit,
    forms,
    accessToken,
  }: {
    audit: SerializedAudit;
    forms: Array<AuditForm>;
    accessToken: string | undefined;
  }): Promise<SyncAuditResult> {
    const response = await fetch(`${this.opts.baseUrl}/v1/audits`, {
      method: 'PATCH',
      headers: this.buildHeaders(accessToken, {
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ audit, forms }),
    });

    if (!response.ok) {
      throw new Error(
        `Audit sync failed: ${response.status} ${response.statusText}`,
      );
    }

    const body = await response.json();
    return body.data as SyncAuditResult;
  }

  async getFileUploadUrl({
    serverAuditId,
    fileId,
    accessToken,
    replace = true,
  }: {
    serverAuditId: string;
    fileId: string;
    accessToken: string | undefined;
    replace?: boolean;
  }): Promise<string> {
    const response = await fetch(
      `${this.opts.baseUrl}/v1/files/${serverAuditId}/${encodeURIComponent(fileId)}?replace=${replace}`,
      {
        method: 'POST',
        headers: this.buildHeaders(accessToken),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Get file upload URL failed: ${response.status} ${response.statusText}`,
      );
    }

    const body = await response.json();
    return body.data.url as string;
  }

  async uploadFileToUrl({
    url,
    content,
    mimeType,
  }: {
    url: string;
    content: FileContent;
    mimeType?: string;
  }): Promise<void> {
    const response = await fetch(url, {
      method: 'PUT',
      headers: mimeType ? { 'Content-Type': mimeType } : {},
      body:
        content instanceof Blob
          ? content
          : new Uint8Array(content as ArrayBuffer),
    });

    if (!response.ok) {
      throw new Error(
        `File upload failed: ${response.status} ${response.statusText}`,
      );
    }
  }
}

export const syncApiService = new SyncApiService({
  baseUrl: publicApiConfig.baseUrl,
});
